import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  useTheme,
} from "@mui/material";
import useMetadata from "app/hooks/useMetadata";
import useJumboLayoutSidebar from "@jumbo/hooks/useJumboLayoutSidebar";
import useAdmin from "app/routes/middleware/Admin/useAdmin";
import programmingContentService from "app/services/programming-content-service";
import { ROUTES } from "app/utils/constants/routeConstants";
import { ASSET_PROGRAMMING_LANGUAGES } from "app/utils/constants/paths";
import {
  ALL_ENTITIES,
  ProgrammingContentEntity,
} from "app/utils/constants/entityConstants";

const ProgrammingLanguageCard = ({
  title,
  description,
  icon,
  onClick,
  customRender,
}) => {
  const theme = useTheme();

  // If customRender is provided, use it to render the card content
  if (customRender) {
    return customRender({ theme, onClick });
  }

  return (
    <Card
      sx={{
        borderRadius: 2,
        textAlign: "center",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        cursor: "pointer",
        "&:hover": {
          bgcolor:
            theme.palette.mode === "dark" ? theme.palette.grey[800] : "#F5F7FA",
        },
        bgcolor: theme.palette.background.paper,
      }}
      onClick={onClick}
    >
      <CardContent>
        {icon}
        <Typography variant="h5" gutterBottom color="text.primary">
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </CardContent>
      <CardActions sx={{ justifyContent: "center" }}></CardActions>
    </Card>
  );
};

const ProgrammingLanguagesPage = () => {
  const navigate = useNavigate();
  const { metadata } = useMetadata();
  const { sidebarOptions, setSidebarOptions } = useJumboLayoutSidebar();
  const isAdmin = useAdmin();
  const [loading, setLoading] = useState(false);

  const languages = (metadata?.programmingLanguages || []).map(
    ({ key, value }) => ({
      name: value,
      param: key,
      icon: `${ASSET_PROGRAMMING_LANGUAGES}/${value.toLowerCase()}.png`,
      description: `Master ${value} with hands-on tutorials and coding challenges`,
    })
  );

  const handleLanguageClick = async (language) => {
    if (!sidebarOptions.open) {
      setSidebarOptions({ open: true });
    }

    setLoading(true);
    try {
      const contents =
        await programmingContentService.getProgrammingContentsMenu(
          language.param
        );

      // Find the first content item with status === "ACTIVE"
      const firstActiveContent = contents?.find(
        (content) => content.status === "ACTIVE"
      );
      const firstUrl = firstActiveContent ? firstActiveContent.url : null;

      // Construct the base path
      const basePath = `${
        ROUTES.PROGRAMMING_CONTENT
      }/${language.name.toLowerCase()}`;

      // Navigate to the first active content URL or the base path
      const targetUrl = firstUrl ? `${basePath}/${firstUrl}` : basePath;

      navigate(targetUrl);
    } catch (error) {
      console.error("Failed to fetch programming contents:", error);
      // Fallback to the base language path on error
      navigate(`${ROUTES.PROGRAMMING_CONTENT}/${language.name.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  };

  // Transform languages into dashboard card format
  const languageCards = languages.map((language) => ({
    title: language.name,
    description: loading ? "Loading..." : language.description,
    icon: null, // We'll handle the icon in customRender
    route: null, // We'll handle navigation in customRender
    isAdminOnly: false,
    customRender: ({ theme, onClick }) => (
      <Card
        sx={{
          borderRadius: 2,
          textAlign: "center",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          cursor: "pointer",
          "&:hover": {
            bgcolor:
              theme.palette.mode === "dark"
                ? theme.palette.grey[800]
                : theme.palette.grey[100],
          },
          bgcolor: theme.palette.background.paper,
        }}
        onClick={() => handleLanguageClick(language)}
      >
        <CardContent>
          <Box
            component="img"
            src={language.icon}
            alt={language.name}
            sx={{ width: 40, height: 40, mb: 2 }}
          />
          <Typography variant="h5" gutterBottom color="text.primary">
            {language.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {loading ? "Loading..." : language.description}
          </Typography>
        </CardContent>
        <CardActions sx={{ justifyContent: "center", pb: 2 }}></CardActions>
      </Card>
    ),
  }));

  // Filter cards based on admin status
  const filterCards = (cards) =>
    cards.filter((card) => !card.isAdminOnly || (card.isAdminOnly && isAdmin));

  // Dynamically create dashboard sections from ALL_ENTITIES
  const allDashboardSections = ALL_ENTITIES.map((entity) => {
    const cards = filterCards(entity.dashboardCards || []);

    // Append languageCards for ProgrammingContentEntity if admin
    if (entity === ProgrammingContentEntity && isAdmin) {
      cards.push(...languageCards);
    }

    return {
      sectionTitle: entity.sectionTitle,
      cards,
    };
  }).filter((section) => section.cards.length > 0); // Remove sections with no cards

  return (
    <>
      {allDashboardSections.map((section, index) => (
        <Box key={index} sx={{ p: 3 }}>
          <Typography variant="h1" gutterBottom sx={{ pb: 1 }}>
            {section.sectionTitle}
          </Typography>
          <Grid container spacing={3}>
            {section.cards.map((card, cardIndex) => (
              <Grid item xs={12} sm={6} md={4} key={cardIndex}>
                <ProgrammingLanguageCard
                  title={card.title}
                  description={card.description}
                  icon={card.icon}
                  onClick={card.route ? () => navigate(card.route) : undefined}
                  customRender={card.customRender}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}
    </>
  );
};

export default ProgrammingLanguagesPage;
